import * as React from 'react';
import type { CopyButtonProps } from './types';
import { CopyButtonStateEnum } from './types';
import { CaptionAccentTypography } from '@components/atoms';
import { ButtonVariant } from '@components/molecules';
import { Button } from '@components/molecules/button/button';
import { cn } from '@utils';

const DELAY_DEFAULT = 500;

export const CopyButton: React.FC<CopyButtonProps> = ({
	icon,
	text,
	onClick,
	onCopied,
	onCopyFailed,
	successTitle,
	failedTitle,
	delay,
	...props
}) => {
	const [buttonState, setButtonState] = React.useState(CopyButtonStateEnum.IDLE);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();

		window.navigator.clipboard
			.writeText(text)
			.then(() => {
				setButtonState(CopyButtonStateEnum.COPY_SUCCESS);
				onCopied?.();
			})
			.catch((reason: Error) => {
				setButtonState(CopyButtonStateEnum.COPY_FAIL);
				onCopyFailed?.(reason);
			})
			.finally(() => onClick?.(event));
	};

	React.useEffect(() => {
		let timeout: NodeJS.Timeout;

		if (buttonState !== CopyButtonStateEnum.IDLE) {
			timeout = setTimeout(() => setButtonState(CopyButtonStateEnum.IDLE), delay ?? DELAY_DEFAULT);
		}

		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [buttonState, delay, successTitle]);

	const title: string | undefined =
		buttonState === CopyButtonStateEnum.IDLE
			? props.title
			: buttonState === CopyButtonStateEnum.COPY_SUCCESS
				? successTitle || props.title
				: failedTitle || props.title;

	return (
		<Button
			{...props}
			variant={ButtonVariant.ICON}
			className={cn('hover:cursor-pointer', props.className)}
			onClick={handleClick}
			asChild
		>
			<div className="flex items-center gap-0.5">
				<span className="flex items-center justify-center w-4 h-4">{icon}</span>
				{title && <CaptionAccentTypography className="text-primary">{title}</CaptionAccentTypography>}
			</div>
		</Button>
	);
};
